import type {
  EventEditingLocaleText,
  EventCalendarLocaleText,
  EventTimelineLocaleText,
  SchedulerEventLocaleText,
} from '../models/translations';
import { getSchedulerLocalization } from '../utils/getSchedulerLocalization';
import type { SchedulerLocalization } from '../utils/getSchedulerLocalization';

const enUSDialog: EventEditingLocaleText = {
  // EventDialog
  colorPickerLabel: 'Event color',
  colorSectionLabel: 'Color',
  dateTimeSectionLabel: 'Date & time',
  resourceColorSectionLabel: 'Resource & color',
  allDayLabel: 'All Day',
  closeButtonAriaLabel: 'Close',
  closeButtonLabel: 'Close',
  editEventButtonAriaLabel: 'Edit event',
  deleteEventButtonAriaLabel: 'Delete event',
  eventActionsToolbarAriaLabel: 'Event actions',
  deleteEvent: 'Delete event',
  deleteConfirmationTitle: 'Delete this event?',
  deleteConfirmationMessage: 'This action is irreversible. Are you sure you want to proceed?',
  cancel: 'Cancel',
  editEvent: 'Edit event',
  showEventDetails: 'Show details',
  eventContextMenuAriaLabel: 'Event actions',
  descriptionLabel: 'Description',
  endDateLabel: 'End date',
  endTimeLabel: 'End time',
  eventTitleAriaLabel: 'Event title',
  generalTabLabel: 'General',
  labelNoResource: 'No resource',
  labelInvalidResource: 'Invalid resource',
  recurrenceLabel: 'Recurrence',
  recurrenceNoRepeat: "Don't repeat",
  recurrenceCustomRepeat: 'Custom repeat rule',
  recurrenceDailyPresetLabel: 'Repeats daily',
  recurrenceDailyFrequencyLabel: 'days',
  recurrenceEndsLabel: 'Ends',
  recurrenceEndsAfterLabel: 'After',
  recurrenceEndsNeverLabel: 'Never',
  recurrenceEndsUntilLabel: 'Until',
  recurrenceEndsTimesLabel: 'times',
  recurrenceEveryLabel: 'Every',
  recurrenceRepeatLabel: 'Repeat',
  recurrenceTabLabel: 'Recurrence',
  recurrenceTimezoneLabel: (timezone) => `Timezone: ${timezone}`,
  recurrenceLabelTimezoneSuffix: (timezone) => `(${timezone})`,
  recurrenceMainSelectCustomLabel: 'Recurrence',
  recurrenceWeeklyFrequencyLabel: 'weeks',
  recurrenceWeeklyPresetLabel: ({ weekdayName }) => `Repeats weekly on ${weekdayName}`,
  recurrenceMonthlyFrequencyLabel: 'months',
  recurrenceMonthlyDayOfMonthLabel: (dayNumber) => `Day ${dayNumber}`,
  recurrenceMonthlyLastWeekAriaLabel: ({ weekdayName }) =>
    `${weekdayName} of the last week of the month`,
  recurrenceMonthlyLastWeekLabel: ({ weekdayName }) => `${weekdayName} last week`,
  recurrenceMonthlyPresetLabel: (dayNumber) => `Repeats monthly on day ${dayNumber}`,
  recurrenceMonthlyWeekNumberAriaLabel: ({ ord, weekdayName }) =>
    `${weekdayName} week ${ord} of the month`,
  recurrenceMonthlyWeekNumberLabel: ({ ord, weekdayName }) => `${weekdayName} week ${ord}`,
  recurrenceWeeklyMonthlySpecificInputsLabel: 'On',
  recurrenceYearlyFrequencyLabel: 'years',
  recurrenceYearlyPresetLabel: (date) => `Repeats annually on ${date}`,
  noResourceAriaLabel: 'No specific resource',
  selectColorAriaLabel: (color) => `Select ${color} as event color`,
  resourceLabel: 'Resource',
  invalidDateError: 'Enter a valid date.',
  invalidTimeError: 'Enter a valid time.',
  requiredResourceError: 'A resource is required.',
  saveChanges: 'Save',
  startDateAfterEndDateError: 'End date cannot be before start date.',
  startDateLabel: 'Start date',
  startTimeAfterEndTimeError: 'End time must be after start time.',
  startTimeLabel: 'Start time',

  // RecurringScopeDialog
  all: 'All events',
  confirm: 'Confirm',
  onlyThis: 'Only this event',
  radioGroupAriaLabel: 'Editing recurring events scope',
  thisAndFollowing: 'This and following events',
  title: 'Apply this change to:',
};

const enUSEvent: SchedulerEventLocaleText = {
  // Event accessible name
  eventAriaLabelTimeRange: (start, end) => `${start} to ${end}`,
  eventAriaLabelDateRange: (start, end) => `From ${start} to ${end}`,
  eventAriaLabelAllDay: 'All day',
  eventAriaLabelRecurring: 'Recurring',
  resourceAriaLabel: (resourceName) => `Resource: ${resourceName}`,
  eventAriaLabel: ({ title, when, date, resource, recurring }) =>
    [title, when, date, resource, recurring].filter(Boolean).join(', '),
};

const enUSCalendar: Omit<
  EventCalendarLocaleText,
  keyof EventEditingLocaleText | keyof SchedulerEventLocaleText
> = {
  // ResourcesTree
  resourcesLabel: 'Resources',

  // ViewSwitcher
  agenda: 'Agenda',
  day: 'Day',
  month: 'Month',
  other: 'Other',
  today: 'Today',
  week: 'Week',
  time: 'Time',
  days: 'Days',
  months: 'Months',
  weeks: 'Weeks',
  years: 'Years',

  // DateNavigator
  closeSidePanel: 'Close side panel',
  openSidePanel: 'Open side panel',

  // SidePanelDrawer (small screens)
  openMenu: 'Open menu',

  // Preferences menu
  amPm12h: '12-hour (1:00PM)',
  hour24h: '24-hour (13:00)',
  preferencesMenu: 'Preferences',
  showWeekends: 'Show weekends',
  showEmptyDaysInAgenda: 'Show empty days',
  showWeekNumber: 'Show week number',
  timeFormat: 'Time format',
  viewSpecificOptions: (view) => `${view} view options`,
  startWeekOn: 'Start week on',
  weekdaySunday: 'Sunday',
  weekdayMonday: 'Monday',
  weekdaySaturday: 'Saturday',

  // WeekView
  allDay: 'All day',

  hiddenEvents: (hiddenEventsCount) => `${hiddenEventsCount} more..`,
  nextTimeSpan: (timeSpan) => `Next ${timeSpan}`,
  previousTimeSpan: (timeSpan) => `Previous ${timeSpan}`,
  weekAbbreviation: 'W',
  weekNumberAriaLabel: (weekNumber) => `Week ${weekNumber}`,

  // AgendaView
  agendaViewEmptyStateLabel: 'No upcoming events',

  // EventItem
  eventItemMultiDayLabel: (endDate) => `Ends ${endDate}`,

  // MiniCalendar
  miniCalendarLabel: 'Calendar',
  miniCalendarGoToPreviousMonth: 'Show previous month in calendar',
  miniCalendarGoToNextMonth: 'Show next month in calendar',

  // Main calendar region
  calendarContentAriaLabel: 'Calendar content',

  // Timeline title sub grid
  timelineResourceTitleHeader: 'Resource title',
};

const enUSTimeline: Omit<
  EventTimelineLocaleText,
  keyof EventEditingLocaleText | keyof SchedulerEventLocaleText
> = {
  // Timeline title sub grid
  timelineResourceTitleHeader: 'Resource title',
};

export const enUS: SchedulerLocalization = getSchedulerLocalization({
  dialog: enUSDialog,
  event: enUSEvent,
  calendar: enUSCalendar,
  timeline: enUSTimeline,
});
